<script setup lang="ts">
import {
  onMounted,
  ref,
  useMetrics,
  useNuxtApp,
  useRouter,
  useRuntimeConfig,
  useSupabaseClient,
  useTemplateRef,
  useToast,
  useUser,
  watch,
  type Creator,
} from "#imports";
import { isTrue, isRequired, isEmail } from "~/components/forms/validation";
import MPLoader from "../widgets/MPLoader.vue";
import MPInput from "../forms/_MPInput.vue";
import MPAuth from "../dialogs/MPAuth/MPAuth.vue";
import MPButton from "../forms/MPButton.vue";
import MPCheckbox from "../forms/MPCheckbox.vue";
import type {
  StripeCheckout,
  StripeCheckoutElementsOptions,
} from "@stripe/stripe-js/dist/stripe-js";

const route = useRouter();
const supabase = useSupabaseClient();
const config = useRuntimeConfig();
const userStore = useUser();
const nuxtApp = useNuxtApp();
const stripe = await nuxtApp.$stripe;
const { showError } = useToast();
const emailInput = useTemplateRef("emailInput");

let checkout!: StripeCheckout;
const paymentClientKey = ref<string | null>(null);
const loadingPaymentForm = ref<boolean>(true);

const props = defineProps<{
  creator: Creator;
  tier: string;
  selectedPriceId: string;
}>();

const createCheckoutSession = async () => {
  const { data, error } = await supabase.functions.invoke(
    "payments/create-checkout-session",
    {
      method: "POST",
      body: {
        type: props.tier,
        creatorId: props.creator?.id || null,
        priceId: props.selectedPriceId,
        redirectUrl: `${config.public.baseURL + route.currentRoute.value.fullPath}?payment=success&tier=${props.tier}`,
      },
    },
  );
  if (error) {
    showError(error);
  }
  paymentClientKey.value = data.clientSecret;
};

const stripeOptions: StripeCheckoutElementsOptions = {
  appearance: {
    theme: "stripe",
    // customFontSource: 'https://fonts.googleapis.com/css2?family=Uncut+Sans:wght@400;500;600;700&display=swap',
    variables: {
      fontFamily: "Uncut Sans, sans-serif",
      colorPrimary: "#f33f5e",
      colorBackground: "#ffffff",
      colorText: "#30313d",
      colorDanger: "#df1b41",
      spacingUnit: "4px",
      fontSizeBase: "16px",
    },
    // https://docs.stripe.com/payments/checkout/customization/appearance?payment-ui=embedded-components
    rules: {
      ".Label": {
        fontWeight: "600",
        fontSize: "16px",
        color: "#475569",
        margin: "0 0 0.5rem 0",
      },
      ".Input": {
        fontWeight: "400",
        fontSize: "16px",
        color: "#64748a",
        border: "1px solid #f0f4f8",
        boxShadow: "0 0 0 1px #e1e7ef, 0 2px 2px 0 #f0f4f8",
      },
      ".Input--invalid": {
        border: "1px solid #e01d48",
      },
      ".Error": {
        color: "#e01d48",
        fontWeight: "400",
        fontSize: "0.9rem",
      },
    },
  },
};
const { trackEvent } = useMetrics();
const paymentPending = ref(false);
const checkoutIsLoading = ref(true);
const privacyPolicyAccepted = ref(false);
onMounted(async () => {
  await createCheckoutSession();
  checkout = await stripe!.initCheckout({
    clientSecret: paymentClientKey.value!,
    elementsOptions: stripeOptions,
  });

  const paymentElement = checkout.createElement("payment", {
    layout: "tabs",
    fields: {
      billingDetails: {
        address: {
          line1: "never",
          line2: "never",
          city: "never",
          state: "never",
          postalCode: "auto",
          country: "auto",
        },
      },
    },
  });
  paymentElement.mount("#payment-element");

  const button = document.getElementById("pay-button")!;
  const errors = document.getElementById("confirm-errors")!;
  // todo handle error text
  if (!button) {
    return;
  }
  button.addEventListener("click", async () => {
    paymentPending.value = true;
    if (!userStore.user.id) {
      emailInput.value!.validate();
      await checkout.updateEmail(userEmail.value!);
    } else {
      await checkout.updateEmail(userStore.user.email!);
    }
    checkout.confirm().then((result) => {
      if (result.type === "error") {
        errors.textContent = result.error.message;
      } else {
        trackEvent("subscribed_paid", {
          subscription_tier: props.tier,
        });
      }
      paymentPending.value = false;
    });
  });
  checkoutIsLoading.value = false;
});

// Handle Auth Flow
const userEmail = ref<string>("");
const loginIsVisible = ref(false);
const stripeForm = ref<HTMLFormElement | null>(null);

watch(loginIsVisible, () => {
  if (loginIsVisible.value) {
    stripeForm.value!.style.opacity = "0";
  } else {
    stripeForm.value!.style.opacity = "1";
  }
});
</script>

<template>
  <div>
    <h4 v-if="!loadingPaymentForm">
      Support <span>@{{ creator.mappr_handle }}<span /></span>
    </h4>
    <div v-if="checkoutIsLoading" class="loading">
      <MPLoader inline label="Loading checkout" />
    </div>
    <form id="payment-form" ref="stripeForm" @submit.prevent>
      <div
        v-if="!userStore.user.id && !loginIsVisible && !checkoutIsLoading"
        class="user-info"
      >
        <MPInput
          ref="emailInput"
          v-model="userEmail"
          name="email"
          label="Create an account"
          type="email"
          placeholder="Enter your Email"
          :validate="(v: any) => isRequired(v) || isEmail(v)"
        />
        <div class="privacy-policy">
          <MPCheckbox
            v-model="privacyPolicyAccepted"
            name="privacyPolicyAccepted"
            label="I agree to the"
            :validate="
              (v: boolean) =>
                isTrue(v) ? 'Privacy policy is required' : undefined
            "
          >
            <template #helpText>
              <NuxtLink to="/info/privacy-policy">Privacy Policy</NuxtLink>
            </template>
          </MPCheckbox>
          <!-- <span @click="openPrivacyModal" class="typeface-regular"
          ><i class="ri-information-2-line" />&nbsp;Info</span
        > -->
        </div>
        <small
          >Already registered?
          <a href="#" @click="loginIsVisible = true"
            >Click here to login</a
          ></small
        >
      </div>
      <div v-if="loginIsVisible">
        <a
          href="#"
          class="back-to-payment-button"
          @click="loginIsVisible = false"
        >
          <i class="ri-arrow-left-line" />&nbsp;Back to payment
        </a>
        <br />
        <MPAuth
          title="Login"
          summary="Login to continue, or click back to payment to create an account"
          @onAuthSuccess="loginIsVisible = true"
        />
      </div>

      <div id="payment-element" />
      <div id="confirm-errors" />
      <MPButton
        type="submit"
        id="pay-button"
        label="Pay & Subscribe"
        version="primary"
        :disabled="paymentPending || checkoutIsLoading"
        :isLoading="paymentPending"
      />
    </form>
  </div>
</template>

<style lang="postcss" scoped>
.user-info {
  text-align: left;
  display: grid;
  gap: 0.5rem;
}

#payment-form {
  text-align: left;
  display: grid;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1.5rem;
  max-width: 30rem;
  margin: 0 auto 1rem;
}

small {
  color: var(--color-slate-400);
  line-height: 1.5;
  a {
    color: var(--color-indigo-400);
  }
}

.privacy-policy {
  position: relative;
  align-items: center;
  justify-items: start;
  gap: 0.5rem;
  font-size: var(--font-size-9);

  span {
    position: absolute;
    top: 50%;
    right: -1.5rem;
    transform: translate(-50%, -50%);
    color: var(--color-slate-500);
    background-color: var(--color-slate-50);
    padding: 0.25rem 0.25rem;
    border-radius: 0.25rem;
    cursor: pointer;
  }
}

.back-to-payment-button {
  display: block;
  background-color: var(--color-slate-100);
  padding: 1rem 1rem;
  border-radius: 0.5rem;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
}
#confirm-errors {
  color: var(--color-rose-600);
  margin-top: 0.5rem;
  margin: 0 auto;
  border-radius: 0.5rem;
}
.checkout {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

#payment-element {
  margin-bottom: 1.5rem;
}
span {
  color: var(--color-rose-500);
}
.loading {
  color: var(--color-slate-400);
  padding-top: 3rem;
  top: 3rem;
  i {
    position: absolute;
    font-size: 1.5rem;
    top: 1rem;
    left: 50%;
    width: 1.5re;
    height: 1.5re;
    margin: -0.75rem 0 0 -0.75rem;
    animation: spin 4s linear infinite;
  }
}
@keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
